'use client'

import { useState } from 'react'
import {
  Check,
  Copy,
  Cpu,
  Heart,
  Laptop,
  Radio,
  Sparkles,
  WalletCards,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ContentPage } from '@/components/content-page'

const ZELLE_ADDRESS = 'rick.lopez@icloud.com'

const supportUses = [
  {
    icon: Radio,
    title: 'Radio testing',
    description: 'Purchasing radios, cables, and adapters that are not yet supported.',
  },
  {
    icon: Laptop,
    title: 'Legacy test systems',
    description: 'Older Windows, macOS, and Linux computers used to reproduce platform issues.',
  },
  {
    icon: Cpu,
    title: 'Development hardware',
    description: 'Test equipment and replacement parts that keep builds reliable.',
  },
  {
    icon: Sparkles,
    title: 'LLM development',
    description: 'The model tokens used extensively while developing and testing NewChirp.',
  },
]

export default function DonatePage() {
  const [copied, setCopied] = useState(false)

  async function copyZelle() {
    try {
      await navigator.clipboard.writeText(ZELLE_ADDRESS)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <ContentPage
      wide
      eyebrow="// support the project"
      title="Donate to NewChirp"
      description="NewChirp is free and open source. Your support helps us test more radios and keep the project moving."
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <div className="grid gap-4 md:grid-cols-2">
          <section className="flex flex-col rounded-2xl border border-border bg-card p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background text-primary">
              <Radio className="h-5 w-5" />
            </span>
            <h2 className="mt-5 text-2xl font-bold tracking-tight">Donate a radio</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              We are looking for radios that may not currently be supported. Unsupported models,
              firmware variants, and related programming cables are especially valuable because
              they let us implement and verify real-world support.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Please contact us before shipping anything so we can confirm whether the equipment
              is useful and arrange delivery.
            </p>
            <Button
              className="mt-5 self-start"
              variant="outline"
              nativeButton={false}
              render={<a href="mailto:rick@newchirp.net?subject=Radio%20donation%20for%20NewChirp" />}
            >
              Offer a radio
            </Button>
          </section>

          <section className="flex flex-col rounded-2xl border border-primary/30 bg-secondary/50 p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-primary/30 bg-background text-primary">
              <WalletCards className="h-5 w-5" />
            </span>
            <h2 className="mt-5 text-2xl font-bold tracking-tight">Financial donations</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              Financial donations are accepted through Zelle. Contributions help purchase radios,
              older computer hardware for Windows/macOS/Linux testing, and the LLM tokens used
              extensively during development.
            </p>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              I prefer to receive donations directly rather than through services such as Buy Me a
              Coffee or Patreon, which take a percentage. That way, the money goes directly toward
              further development of NewChirp. Thank you again to everyone.
            </p>
            <div className="mt-5 rounded-xl border border-border bg-card p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Zelle
              </p>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-sm font-semibold text-foreground">{ZELLE_ADDRESS}</span>
                <Button type="button" variant="outline" size="sm" onClick={copyZelle}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy address'}
                </Button>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Please include “NewChirp donation” in the Zelle memo.
            </p>
          </section>
        </div>

        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-3">
            <Heart className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">What your support pays for</h2>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {supportUses.map((item) => {
              const Icon = item.icon
              return (
                <div key={item.title} className="flex items-start gap-3 rounded-xl bg-secondary/50 p-4">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <h3 className="font-medium text-foreground">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        <p className="text-center text-sm leading-relaxed text-muted-foreground">
          Thank you for helping make NewChirp better for everyone.
        </p>
      </div>
    </ContentPage>
  )
}
